import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Space,
  Empty,
  Tooltip,
  Segmented,
  Alert
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  FilterOutlined,
  GroupOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  MAX_ADVANCED_FILTER_DEPTH,
  normalizeAdvancedFilterGroup,
  countAdvancedFilterRules,
  getAdvancedFilterDepth
} from './gridQuery';

// ─────────────────────────────────────────────────────────────────
// Glass-i — ErpGridAdvancedFilterModal (Stage 6A Increment 3)
//
// Reusable modal for composing a `GridAdvancedFilterGroup` (see
// gridTypes.js). This iteration supports nested AND / OR groups up
// to MAX_ADVANCED_FILTER_DEPTH levels deep so users can express
// `(A AND (B OR C))` queries without hand-editing JSON. The wire
// shape is unchanged — groups carry `combinator` + recursive `rules`
// — so the backend parser / persistence layer needs no migration.
//
// Lifecycle:
//   - `open` controls visibility; the parent toolbar opens it.
//   - `initialGroup` seeds the modal from `query.advancedFilters`.
//   - "Apply" emits the composed (and pruned) group via `onApply(group)`.
//     If nothing survives normalisation, `onApply(null)` is emitted.
//   - "Clear filters" empties the modal AND emits `onApply(null)`.
//   - "Cancel" closes without emitting.
//
// Security note: there is NO `eval` / `new Function` / dynamic
// execution anywhere in this file. Operators are picked from the
// static OPERATORS table; values are bound via React-controlled
// inputs only. The backend parser remains the only place where these
// rules turn into SQL — and that parser allow-lists every field and
// uses $N placeholders for every value.
// ─────────────────────────────────────────────────────────────────

/** @typedef {import('./gridTypes').GridColumn} GridColumn */
/** @typedef {import('./gridTypes').GridFilterOperator} GridFilterOperator */
/** @typedef {import('./gridTypes').GridFilterSpec} GridFilterSpec */
/** @typedef {import('./gridTypes').GridAdvancedFilterGroup} GridAdvancedFilterGroup */

// Operators supported in this Stage. The data-model accepts more
// (see gridTypes.js → GridFilterOperator) but the UI lists this safe
// core only. Extending here is a UI-only change — the backend parser
// already supports them via its OPS_BY_TYPE matrix.
const OPERATORS = [
  { value: 'equals', label: 'equals', types: ['string', 'number', 'date', 'boolean', 'enum'] },
  { value: 'notEquals', label: 'does not equal', types: ['string', 'number', 'date', 'boolean', 'enum'] },
  { value: 'contains', label: 'contains', types: ['string'] },
  { value: 'startsWith', label: 'starts with', types: ['string'] },
  { value: 'endsWith', label: 'ends with', types: ['string'] },
  { value: 'gt', label: 'greater than', types: ['number', 'date'] },
  { value: 'gte', label: 'greater than or equal', types: ['number', 'date'] },
  { value: 'lt', label: 'less than', types: ['number', 'date'] },
  { value: 'lte', label: 'less than or equal', types: ['number', 'date'] },
  { value: 'between', label: 'between', types: ['number', 'date'] },
  { value: 'in', label: 'is one of', types: ['enum', 'string', 'number'] },
  { value: 'isEmpty', label: 'is empty', types: ['string', 'number', 'date', 'boolean', 'enum'] },
  { value: 'isNotEmpty', label: 'is not empty', types: ['string', 'number', 'date', 'boolean', 'enum'] }
];

// Operators that need NO value input.
const NO_VALUE_OPS = new Set(['isEmpty', 'isNotEmpty']);
// Operators that need a TWO-value input (range).
const RANGE_OPS = new Set(['between']);

function dataTypeFor(column) {
  if (!column) return 'string';
  if (column.dataType) return column.dataType;
  // Heuristic fallback when the column doesn't declare one. Mirrors
  // the AntD column filter UX so user expectations line up.
  if (column.filterType === 'number') return 'number';
  if (column.filterType === 'date' || column.filterType === 'dateRange') return 'date';
  if (column.filterType === 'select' || column.filterType === 'multiSelect' || column.filterType === 'status') {
    return 'enum';
  }
  if (column.filterType === 'boolean') return 'boolean';
  return 'string';
}

// UI-only identifier used as a React key on each node in the working
// tree. Stripped before emitting the apply payload (the wire shape
// has no `id` field).
function nodeId() {
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

function blankRule() {
  return { id: nodeId(), field: null, op: 'equals', value: undefined };
}

function blankGroup(combinator = 'and') {
  return { id: nodeId(), combinator, rules: [blankRule()] };
}

// Hydrate a (possibly minimal) wire-shape group into the working tree
// shape used by this component. Adds local `id`s so React can key
// every row stably. Pure; never mutates the input.
function hydrate(node) {
  if (!node || typeof node !== 'object') return blankGroup();
  if (Array.isArray(node.rules)) {
    return {
      id: nodeId(),
      combinator: node.combinator === 'or' ? 'or' : 'and',
      rules: node.rules.length ? node.rules.map(hydrate) : [blankRule()]
    };
  }
  return {
    id: nodeId(),
    field: node.field ?? null,
    op: typeof node.op === 'string' ? node.op : 'equals',
    value: node.value
  };
}

// Strip the local `id`s before emitting upstream. The Apply step also
// runs the result through `normalizeAdvancedFilterGroup` so empty
// nodes are dropped — this helper is purely a shape-cleaning pass.
function strip(node) {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node.rules)) {
    return {
      combinator: node.combinator === 'or' ? 'or' : 'and',
      rules: node.rules.map(strip)
    };
  }
  return {
    field: node.field,
    op: node.op || 'equals',
    value: node.value
  };
}

// Recursive immutable update of a node identified by `targetId`.
// `updater` receives the current node and returns its replacement,
// or `undefined` to remove it from its parent.
function updateNode(root, targetId, updater) {
  if (!root) return root;
  if (root.id === targetId) {
    return updater(root);
  }
  if (Array.isArray(root.rules)) {
    const nextRules = [];
    for (const child of root.rules) {
      if (child.id === targetId) {
        const replaced = updater(child);
        if (replaced !== undefined) nextRules.push(replaced);
      } else if (Array.isArray(child.rules)) {
        const updated = updateNode(child, targetId, updater);
        if (updated !== undefined) nextRules.push(updated);
      } else {
        nextRules.push(child);
      }
    }
    return { ...root, rules: nextRules };
  }
  return root;
}

// Append a child (rule or group) to a specific group identified by id.
function appendChild(root, parentId, child) {
  return updateNode(root, parentId, (g) => {
    if (!Array.isArray(g.rules)) return g;
    return { ...g, rules: [...g.rules, child] };
  });
}

// One row in the modal — a single leaf rule. Pure presentation; all
// mutation goes through the props handlers.
function RuleRow({
  rule,
  columnByKey,
  fieldOptions,
  isFirst,
  combinator,
  onChange,
  onRemove
}) {
  const col = columnByKey.get(rule.field);
  const dataType = dataTypeFor(col);
  const opChoices = useMemo(
    () => OPERATORS.filter((o) => o.types.includes(dataType)),
    [dataType]
  );

  const renderValueEditor = () => {
    if (NO_VALUE_OPS.has(rule.op)) return null;
    if (RANGE_OPS.has(rule.op)) {
      const v = rule.value && typeof rule.value === 'object' ? rule.value : {};
      if (dataType === 'date') {
        return (
          <DatePicker.RangePicker
            size="small"
            value={[v.from ? dayjs(v.from) : null, v.to ? dayjs(v.to) : null]}
            onChange={(range) => {
              onChange({
                value: {
                  from: range?.[0] ? range[0].toISOString() : undefined,
                  to: range?.[1] ? range[1].toISOString() : undefined
                }
              });
            }}
            className="!w-full"
          />
        );
      }
      return (
        <Space.Compact className="!w-full">
          <InputNumber
            size="small"
            placeholder="from"
            value={v.from}
            onChange={(val) => onChange({ value: { ...v, from: val } })}
            className="!w-full"
          />
          <InputNumber
            size="small"
            placeholder="to"
            value={v.to}
            onChange={(val) => onChange({ value: { ...v, to: val } })}
            className="!w-full"
          />
        </Space.Compact>
      );
    }
    if (rule.op === 'in') {
      const opts = col && Array.isArray(col.filters) ? col.filters : null;
      return (
        <Select
          mode={opts ? 'multiple' : 'tags'}
          size="small"
          value={Array.isArray(rule.value) ? rule.value : []}
          onChange={(v) => onChange({ value: v })}
          options={opts ? opts.map((o) => ({ value: o.value, label: o.text })) : undefined}
          placeholder="Pick one or more values"
          className="!w-full"
        />
      );
    }
    switch (dataType) {
      case 'number':
        return (
          <InputNumber
            size="small"
            value={rule.value}
            onChange={(v) => onChange({ value: v })}
            className="!w-full"
            placeholder="value"
          />
        );
      case 'date':
        return (
          <DatePicker
            size="small"
            value={rule.value ? dayjs(rule.value) : null}
            onChange={(d) => onChange({ value: d ? d.toISOString() : null })}
            className="!w-full"
          />
        );
      case 'enum': {
        const opts = col && Array.isArray(col.filters) ? col.filters : null;
        if (opts) {
          return (
            <Select
              size="small"
              value={rule.value}
              onChange={(v) => onChange({ value: v })}
              options={opts.map((o) => ({ value: o.value, label: o.text }))}
              placeholder="Select a value"
              className="!w-full"
            />
          );
        }
        return (
          <Input
            size="small"
            value={rule.value || ''}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder="value"
            allowClear
          />
        );
      }
      case 'boolean':
        return (
          <Select
            size="small"
            value={rule.value}
            onChange={(v) => onChange({ value: v })}
            options={[
              { value: true, label: 'true' },
              { value: false, label: 'false' }
            ]}
            placeholder="value"
            className="!w-full"
          />
        );
      case 'string':
      default:
        return (
          <Input
            size="small"
            value={rule.value || ''}
            onChange={(e) => onChange({ value: e.target.value })}
            placeholder="value"
            allowClear
          />
        );
    }
  };

  const prefixLabel = isFirst ? 'WHERE' : combinator === 'or' ? 'OR' : 'AND';

  return (
    <div className="flex flex-wrap items-center gap-2 border border-surface-200 rounded-md px-2.5 py-2 bg-surface-50/50">
      <span className="text-[11px] uppercase tracking-wider text-ink-500 w-10">
        {prefixLabel}
      </span>
      <Select
        size="small"
        showSearch
        optionFilterProp="label"
        placeholder="Choose column"
        value={rule.field}
        onChange={(v) => onChange({ field: v, op: 'equals', value: undefined })}
        options={fieldOptions.map((f) => ({ value: f.value, label: f.label }))}
        className="!min-w-[180px] flex-1"
      />
      <Select
        size="small"
        value={rule.op}
        disabled={!rule.field}
        onChange={(v) => onChange({ op: v, value: undefined })}
        options={opChoices}
        className="!min-w-[160px]"
      />
      <div className="flex-1 min-w-[180px]">{renderValueEditor()}</div>
      <Tooltip title="Remove condition">
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          aria-label="Remove condition"
          onClick={onRemove}
        />
      </Tooltip>
    </div>
  );
}

// One group card — renders its own header (AND/OR toggle + Add Condition +
// Add Group + Remove). Children are recursively other groups OR leaf
// RuleRows. `depth` is 1-based; the outermost group is depth 1.
function GroupCard({
  group,
  depth,
  isRoot,
  columnByKey,
  fieldOptions,
  onChangeCombinator,
  onAddRule,
  onAddGroup,
  onUpdateChild,
  onRemoveChild,
  onRemoveSelf
}) {
  const atMaxDepth = depth >= MAX_ADVANCED_FILTER_DEPTH;
  // Indent nested groups visually. Root stays flush; each level adds
  // a small border + left padding so the tree is scannable.
  const containerCls = isRoot
    ? 'space-y-2'
    : 'space-y-2 border-l-2 border-brand-200 pl-3 bg-surface-50 rounded-md py-2 pr-2';

  return (
    <div className={containerCls}>
      <div className="flex items-center gap-2">
        <span className="text-[11.5px] uppercase tracking-wider text-ink-500">
          Match
        </span>
        <Segmented
          size="small"
          value={group.combinator === 'or' ? 'or' : 'and'}
          onChange={(v) => onChangeCombinator(v)}
          options={[
            { label: 'ALL (AND)', value: 'and' },
            { label: 'ANY (OR)', value: 'or' }
          ]}
        />
        <span className="text-[11.5px] text-ink-500">of the following</span>
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined />}
            onClick={onAddRule}
            aria-label="Add condition"
          >
            Add condition
          </Button>
          <Tooltip
            title={
              atMaxDepth
                ? `Maximum nesting depth is ${MAX_ADVANCED_FILTER_DEPTH}`
                : 'Add a nested AND/OR group'
            }
          >
            <Button
              size="small"
              type="text"
              icon={<GroupOutlined />}
              onClick={onAddGroup}
              disabled={atMaxDepth}
              aria-label="Add nested group"
            >
              Add group
            </Button>
          </Tooltip>
          {!isRoot ? (
            <Tooltip title="Remove this group">
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                aria-label="Remove group"
                onClick={onRemoveSelf}
              />
            </Tooltip>
          ) : null}
        </div>
      </div>

      {group.rules.length === 0 ? (
        <div className="text-[12px] italic text-ink-400 pl-1">
          No conditions yet — use Add condition or Add group.
        </div>
      ) : (
        group.rules.map((child, idx) =>
          Array.isArray(child.rules) ? (
            <GroupCard
              key={child.id}
              group={child}
              depth={depth + 1}
              isRoot={false}
              columnByKey={columnByKey}
              fieldOptions={fieldOptions}
              onChangeCombinator={(c) =>
                onUpdateChild(child.id, (n) => ({ ...n, combinator: c }))
              }
              onAddRule={() =>
                onUpdateChild(child.id, (n) => ({
                  ...n,
                  rules: [...n.rules, blankRule()]
                }))
              }
              onAddGroup={() =>
                onUpdateChild(child.id, (n) => ({
                  ...n,
                  rules: [...n.rules, blankGroup(n.combinator === 'and' ? 'or' : 'and')]
                }))
              }
              onUpdateChild={(cid, fn) =>
                onUpdateChild(child.id, (n) => {
                  // Recursive: delegate the update to the grandchild path
                  // by walking the subtree ourselves so siblings stay put.
                  return walkUpdate(n, cid, fn);
                })
              }
              onRemoveChild={(cid) =>
                onUpdateChild(child.id, (n) => ({
                  ...n,
                  rules: n.rules.filter((r) => r.id !== cid)
                }))
              }
              onRemoveSelf={() => onRemoveChild(child.id)}
            />
          ) : (
            <RuleRow
              key={child.id}
              rule={child}
              columnByKey={columnByKey}
              fieldOptions={fieldOptions}
              isFirst={idx === 0}
              combinator={group.combinator}
              onChange={(patch) =>
                onUpdateChild(child.id, (n) => ({ ...n, ...patch }))
              }
              onRemove={() => onRemoveChild(child.id)}
            />
          )
        )
      )}
    </div>
  );
}

// In-component recursive helper. Walks the subtree rooted at `node`
// and applies `fn` to whichever descendant has id === `targetId`,
// returning a NEW node. Used by GroupCard so a child group can patch
// any descendant of itself without lifting state to the modal.
function walkUpdate(node, targetId, fn) {
  if (!node) return node;
  if (node.id === targetId) return fn(node);
  if (!Array.isArray(node.rules)) return node;
  return {
    ...node,
    rules: node.rules.map((c) => walkUpdate(c, targetId, fn))
  };
}

export default function ErpGridAdvancedFilterModal({
  open,
  onClose,
  onApply,
  /** @type {GridColumn[]} */
  columns = [],
  /** @type {GridAdvancedFilterGroup|null|undefined} */
  initialGroup
}) {
  // Working tree is a single recursive group. The root is always a
  // group; leaves are field rules.
  const [tree, setTree] = useState(() =>
    initialGroup ? hydrate(initialGroup) : blankGroup()
  );

  // Re-seed from the parent's current advancedFilters whenever the
  // modal is opened. Avoids resurrecting stale state from a prior
  // session.
  useEffect(() => {
    if (!open) return;
    setTree(initialGroup ? hydrate(initialGroup) : blankGroup());
  }, [open, initialGroup]);

  // Filterable column choices — anything that isn't explicitly opted
  // out via `filterable: false` and that exposes a stable key.
  const fieldOptions = useMemo(() => {
    const out = [];
    const walk = (cols) => {
      for (const c of cols || []) {
        if (Array.isArray(c.children) && c.children.length) {
          walk(c.children);
          continue;
        }
        if (c.filterable === false) continue;
        const key = c.key || c.dataIndex;
        if (!key || c.key === '__erp_actions__') continue;
        out.push({
          value: typeof key === 'string' ? key : String(key),
          label: typeof c.title === 'string' ? c.title : String(key),
          column: c
        });
      }
    };
    walk(columns);
    return out;
  }, [columns]);

  const columnByKey = useMemo(() => {
    const m = new Map();
    fieldOptions.forEach((f) => m.set(f.value, f.column));
    return m;
  }, [fieldOptions]);

  // ── tree mutations ───────────────────────────────────────────
  const setRootCombinator = useCallback((combinator) => {
    setTree((t) => ({ ...t, combinator }));
  }, []);
  const addRootRule = useCallback(() => {
    setTree((t) => ({ ...t, rules: [...t.rules, blankRule()] }));
  }, []);
  const addRootGroup = useCallback(() => {
    setTree((t) => ({
      ...t,
      rules: [...t.rules, blankGroup(t.combinator === 'and' ? 'or' : 'and')]
    }));
  }, []);
  const updateChild = useCallback((childId, fn) => {
    setTree((t) => walkUpdate(t, childId, fn));
  }, []);
  const removeChild = useCallback((childId) => {
    setTree((t) => updateNode(t, t.id, (root) => ({
      ...root,
      rules: root.rules.filter((c) => c.id !== childId)
    })));
  }, []);

  const handleClearAll = useCallback(() => {
    setTree(blankGroup());
  }, []);

  // ── apply / clear ───────────────────────────────────────────
  const handleApply = useCallback(() => {
    const normalised = normalizeAdvancedFilterGroup(strip(tree));
    onApply(normalised);
    onClose();
  }, [tree, onApply, onClose]);

  const handleClear = useCallback(() => {
    setTree(blankGroup());
    onApply(null);
    onClose();
  }, [onApply, onClose]);

  // Live preview counters — useful feedback so users understand the
  // shape they're authoring before they hit Apply.
  const ruleCount = useMemo(() => countAdvancedFilterRules(strip(tree)), [tree]);
  const depth = useMemo(() => getAdvancedFilterDepth(strip(tree)), [tree]);
  const atMaxDepth = depth >= MAX_ADVANCED_FILTER_DEPTH;

  return (
    <Modal
      title={
        <Space size={8}>
          <FilterOutlined />
          <span>Advanced Filter</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={840}
      destroyOnClose
      maskClosable={false}
      footer={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={handleClear} aria-label="Clear advanced filter">
              Clear filters
            </Button>
            <span className="text-[11.5px] text-ink-500 tabular-nums">
              {ruleCount} {ruleCount === 1 ? 'rule' : 'rules'} · depth {depth}/
              {MAX_ADVANCED_FILTER_DEPTH}
            </span>
          </div>
          <Space>
            <Button onClick={onClose} aria-label="Cancel advanced filter">
              Cancel
            </Button>
            <Button type="primary" onClick={handleApply} aria-label="Apply advanced filter">
              Apply
            </Button>
          </Space>
        </div>
      }
    >
      {fieldOptions.length === 0 ? (
        <Empty description="No filterable columns available." />
      ) : (
        <div className="space-y-3">
          {atMaxDepth ? (
            <Alert
              type="info"
              showIcon
              message={`Maximum nesting depth (${MAX_ADVANCED_FILTER_DEPTH}) reached. Add more conditions to existing groups instead of nesting further.`}
            />
          ) : null}
          <GroupCard
            group={tree}
            depth={1}
            isRoot
            columnByKey={columnByKey}
            fieldOptions={fieldOptions}
            onChangeCombinator={setRootCombinator}
            onAddRule={addRootRule}
            onAddGroup={addRootGroup}
            onUpdateChild={updateChild}
            onRemoveChild={removeChild}
            onRemoveSelf={handleClearAll}
          />
        </div>
      )}
    </Modal>
  );
}
